import React, { useMemo, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Download } from "lucide-react";
import { LeadDrawer } from "../LeadDrawer";
import { LeadsChart } from "../LeadsChart";
import { TopPackagers } from "../TopPackagers";
import { KpiCards } from "./KpiCards";
import { LeadsFilterBar } from "./LeadsFilterBar";
import { LeadsTable } from "./LeadsTable";
import { useLeadsData } from "../../hooks/useLeadsData";

const matchesQuery = (lead, query) =>
  [lead.full_name, lead.business_name, lead.email, lead.phone, lead.loan_amount, lead.referred_by, lead.packager]
    .some((v) => v && v.toLowerCase().includes(query));

export const LeadsTab = () => {
  const { leads, stats, caseManagers, setCaseManagers, load, exportCsv, changeStatus, changeCaseManager } = useLeadsData();
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [packagerFilter, setPackagerFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const filtered = useMemo(() => {
    if (!leads) return null;
    const q = query.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "All" && l.status !== statusFilter) return false;
      if (packagerFilter && l.packager !== packagerFilter) return false;
      if (!q) return true;
      return matchesQuery(l, q);
    });
  }, [leads, query, statusFilter, packagerFilter]);

  const selectedLead = leads?.find((l) => l.id === selectedId) || null;

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Leads</h1>
        <button
          onClick={exportCsv}
          disabled={!leads || leads.length === 0}
          data-testid="export-csv-btn"
          className="flex items-center gap-2 rounded-full bg-[#052F24] text-[#DFFF00] px-5 py-2.5 text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Download size={16} /> Export CSV
        </button>
      </div>

      <KpiCards stats={stats} />

      <LeadsChart leads={leads} />

      <TopPackagers leads={leads} selected={packagerFilter} onSelect={setPackagerFilter} />

      <LeadsFilterBar
        query={query}
        onQueryChange={setQuery}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        packagerFilter={packagerFilter}
        onClearPackagerFilter={() => setPackagerFilter("")}
      />

      <LeadsTable
        leads={leads}
        filtered={filtered}
        caseManagers={caseManagers}
        viewerRole="admin"
        onSelect={setSelectedId}
        onStatusChange={changeStatus}
        onCaseManagerChange={changeCaseManager}
        onCaseManagersOptionsChange={setCaseManagers}
      />

      <AnimatePresence>
        {selectedLead && (
          <LeadDrawer
            lead={selectedLead}
            onClose={() => setSelectedId(null)}
            onStatusChange={changeStatus}
            onRefresh={load}
            caseManagers={caseManagers}
            onCaseManagerChange={changeCaseManager}
            onCaseManagersOptionsChange={setCaseManagers}
            viewerRole="admin"
          />
        )}
      </AnimatePresence>
    </div>
  );
};
